import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';

export default function TermLifeIndianapolisPage() {
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    // SEO Meta Tags
    const title = "Term Life Insurance Indianapolis Marion County | Quotes from $15/mo | Hoosier Life Insurance";
    const description = "Affordable term life insurance for Indianapolis and Marion County families. Compare 20+ carriers from $15/month. Serving Carmel, Fishers, Noblesville & all Indianapolis neighborhoods. Call (317) 648-2983.";
    const keywords = "term life insurance Indianapolis, Indianapolis term life, affordable term life Indianapolis, Marion County life insurance, term life quotes Indianapolis, Indianapolis life insurance agent";
    const canonical = "https://hoosierlifeinsurance.com/term-life-indianapolis";

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://hoosierlifeinsurance.com/og-image.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' }
    ];

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = canonical;
    document.head.appendChild(linkCanonical);

    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.name = tag.name;
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.content = tag.content;
      document.head.appendChild(meta);
    });

    // JSON-LD Schemas
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": title,
        "description": description,
        "url": canonical,
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Hoosier Life Insurance",
          "url": "https://hoosierlifeinsurance.com"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Term Life Insurance",
        "provider": {
          "@type": "InsuranceAgency",
          "name": "Hoosier Life Insurance",
          "telephone": "+1-317-648-2983",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Indianapolis",
            "addressRegion": "IN",
            "addressCountry": "US"
          }
        },
        "areaServed": {
          "@type": "City",
          "name": "Indianapolis",
          "containedIn": {
            "@type": "AdministrativeArea",
            "name": "Marion County"
          }
        },
        "offers": {
          "@type": "Offer",
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "15.00",
            "priceCurrency": "USD",
            "unitText": "MONTH"
          }
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "127",
          "bestRating": "5"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://hoosierlifeinsurance.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Term Life Insurance",
            "item": "https://hoosierlifeinsurance.com/term-life"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Indianapolis",
            "item": canonical
          }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much does term life insurance cost in Indianapolis?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Term life insurance in Indianapolis typically costs $15-$50 per month for healthy adults. A 35-year-old Indianapolis resident can get $500,000 of 20-year term coverage for around $25-$30/month. Marion County residents often qualify for Indiana's competitive rates, which are 8-12% below the national average."
            }
          },
          {
            "@type": "Question",
            "name": "Can I get term life insurance without a medical exam in Indianapolis?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes! We offer no-exam term life insurance policies for Indianapolis residents. Coverage up to $500,000 is available with simplified underwriting—just answer health questions, no blood test or physical required. Approval often happens within 24-48 hours."
            }
          },
          {
            "@type": "Question",
            "name": "Do Eli Lilly employees need supplemental term life insurance?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Most Eli Lilly employees receive group life insurance equal to 1-2x their salary. However, this coverage is often insufficient for families with mortgages, children, or significant debt. We help Eli Lilly employees supplement their employer coverage with affordable personal term policies that stay with them even if they change jobs."
            }
          },
          {
            "@type": "Question",
            "name": "What protections does Indiana law provide for life insurance beneficiaries?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Indiana law protects life insurance death benefits from creditors in most cases. Proceeds paid to named beneficiaries are generally exempt from the deceased's debts and cannot be seized by creditors. Indiana also has a 30-day free look period—you can cancel any new policy within 30 days for a full refund."
            }
          },
          {
            "@type": "Question",
            "name": "How fast can I get approved for term life insurance in Indianapolis?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "With no-exam policies, Indianapolis residents can get approved in 24-48 hours. Traditional term policies with medical exams typically take 4-6 weeks. We work with carriers that offer accelerated underwriting for healthy applicants—many of our Indianapolis clients get same-day approvals."
            }
          },
          {
            "@type": "Question",
            "name": "Should Indianapolis families choose term or whole life insurance?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "For most Indianapolis families, term life insurance is the best choice. It provides maximum coverage at the lowest cost during your working years when your family depends on your income. Term is ideal for covering mortgages, children's education, and income replacement. Whole life makes sense for estate planning, business succession, or supplemental retirement income."
            }
          }
        ]
      }
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    // Cleanup
    return () => {
      document.head.removeChild(linkCanonical);
      document.head.removeChild(scriptTag);
      metaTags.forEach(() => {
        const metas = document.head.querySelectorAll('meta[name], meta[property]');
        metas.forEach(meta => {
          const nameOrProp = meta.getAttribute('name') || meta.getAttribute('property');
          if (metaTags.some(t => t.name === nameOrProp || t.property === nameOrProp)) {
            document.head.removeChild(meta);
          }
        });
      });
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (iframeLoading) {
        setIframeError(true);
        setIframeLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [iframeLoading]);

  const coverageOptions = [
    {
      term: '10-Year Term',
      coverage: '$500,000',
      monthlyRate: '$15',
      bestFor: 'Short-term needs, debt coverage',
      icon: 'ri-time-line'
    },
    {
      term: '20-Year Term',
      coverage: '$500,000',
      monthlyRate: '$25',
      bestFor: 'Mortgage protection, growing families',
      icon: 'ri-home-heart-line'
    },
    {
      term: '30-Year Term',
      coverage: '$500,000',
      monthlyRate: '$40',
      bestFor: 'Long-term income replacement',
      icon: 'ri-shield-check-line'
    },
    {
      term: 'No-Exam Term',
      coverage: 'Up to $500,000',
      monthlyRate: '$30',
      bestFor: 'Fast approval, no medical exam',
      icon: 'ri-flashlight-line'
    }
  ];

  const benefits = [
    {
      icon: 'ri-home-4-line',
      title: 'Mortgage Protection',
      description: 'Ensure your Indianapolis home stays in the family even if the unexpected happens. Cover your mortgage balance so your loved ones never face foreclosure.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Income Replacement',
      description: 'Replace 5-10 years of income so your family maintains their Marion County lifestyle—covering bills, groceries, and daily expenses without financial stress.'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: "Children's Education",
      description: 'Fund college tuition at IU, Purdue, Butler, or IUPUI. Protect your children\'s educational future regardless of what happens to you.'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Business Protection',
      description: 'Indianapolis business owners use term life for buy-sell agreements, key person insurance, and protecting business loans from affecting family assets.'
    }
  ];

  const neighborhoods = [
    { name: 'Downtown Indianapolis', slug: 'indianapolis' },
    { name: 'Broad Ripple', slug: 'indianapolis' },
    { name: 'Carmel', slug: 'carmel' },
    { name: 'Fishers', slug: 'fishers' },
    { name: 'Noblesville', slug: 'noblesville' },
    { name: 'Westfield', slug: 'westfield' },
    { name: 'Greenwood', slug: 'indianapolis' },
    { name: 'Lawrence', slug: 'indianapolis' },
    { name: 'Castleton', slug: 'indianapolis' },
    { name: 'Geist', slug: 'indianapolis' },
    { name: 'Speedway', slug: 'indianapolis' },
    { name: 'Beech Grove', slug: 'indianapolis' },
    { name: 'Avon', slug: 'indianapolis' },
    { name: 'Zionsville', slug: 'zionsville' },
    { name: 'Plainfield', slug: 'indianapolis' },
    { name: 'Brownsburg', slug: 'indianapolis' }
  ];

  const faqs = [
    {
      question: 'How much does term life insurance cost in Indianapolis?',
      answer: 'Term life insurance in Indianapolis typically costs $15-$50 per month for healthy adults. A 35-year-old Indianapolis resident can get $500,000 of 20-year term coverage for around $25-$30/month. Marion County residents often qualify for Indiana\'s competitive rates, which are 8-12% below the national average due to our state\'s favorable health demographics and lower cost of living.'
    },
    {
      question: 'Can I get term life insurance without a medical exam in Indianapolis?',
      answer: 'Yes! We offer no-exam term life insurance policies for Indianapolis residents. Coverage up to $500,000 is available with simplified underwriting—just answer health questions, no blood test or physical required. Approval often happens within 24-48 hours, making it perfect for busy Indianapolis professionals who need coverage quickly.'
    },
    {
      question: 'Do Eli Lilly employees need supplemental term life insurance?',
      answer: 'Most Eli Lilly employees receive group life insurance equal to 1-2x their salary. However, this coverage is often insufficient for families with mortgages, children, or significant debt. We help Eli Lilly employees supplement their employer coverage with affordable personal term policies that stay with them even if they change jobs. Plus, personal policies often cost less than you\'d expect and provide portability your group plan doesn\'t offer.'
    },
    {
      question: 'What protections does Indiana law provide for life insurance beneficiaries?',
      answer: 'Indiana law protects life insurance death benefits from creditors in most cases. Proceeds paid to named beneficiaries are generally exempt from the deceased\'s debts and cannot be seized by creditors. Indiana also has a 30-day free look period—you can cancel any new policy within 30 days for a full refund if you change your mind.'
    },
    {
      question: 'How fast can I get approved for term life insurance in Indianapolis?',
      answer: 'With no-exam policies, Indianapolis residents can get approved in 24-48 hours. Traditional term policies with medical exams typically take 4-6 weeks. We work with carriers that offer accelerated underwriting for healthy applicants—many of our Indianapolis clients get same-day approvals and coverage effective within 72 hours.'
    },
    {
      question: 'Should Indianapolis families choose term or whole life insurance?',
      answer: 'For most Indianapolis families, term life insurance is the best choice. It provides maximum coverage at the lowest cost during your working years when your family depends on your income. Term is ideal for covering mortgages, children\'s education, and income replacement. Whole life makes sense for estate planning, business succession, or supplemental retirement income—but term gives you 10-15x more coverage for the same premium.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-500 hover:text-[#1A3A52] transition-colors">
                Home
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/term-life" className="text-gray-500 hover:text-[#1A3A52] transition-colors">
                Term Life Insurance
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-[#1A3A52] font-medium">Indianapolis</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1A3A52] via-[#2A4A62] to-[#1A3A52] text-white py-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/f2ed6d8d040c91200b5e7cd91f94848d.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Term Life Insurance in Indianapolis, Indiana
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-100">
              Affordable Coverage for Marion County Families — Quotes from $15/Month
            </p>
            <p className="text-lg mb-8 text-gray-200 max-w-3xl">
              Protect your Indianapolis family with term life insurance designed for Hoosier households. With Marion County's median income of $61,000 and 980,000+ residents, we help families secure coverage that fits their budget and protects what matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowBookingModal(true)}
                className="bg-[#D97642] hover:bg-[#C86532] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Get Free Quote
              </button>
              <a
                href="tel:+13176482983"
                className="bg-white hover:bg-gray-100 text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg hover:shadow-xl text-center whitespace-nowrap"
              >
                Call (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Local Stats Bar */}
      <section className="bg-gray-50 border-y border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#1A3A52] mb-2">$61,000</div>
              <div className="text-gray-600">Indianapolis Median Household Income</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#1A3A52] mb-2">980,000+</div>
              <div className="text-gray-600">Marion County Population</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#D97642] mb-2">8-12% Lower</div>
              <div className="text-gray-600">Indiana Rates vs. National Average</div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Options Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-4">
              Term Life Insurance Options for Indianapolis Residents
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Sample monthly rates for a healthy 35-year-old Indianapolis resident. Your actual rate depends on age, health, and coverage amount.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coverageOptions.map((option, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className={`${option.icon} text-3xl text-[#D97642]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{option.term}</h3>
                <div className="text-3xl font-bold text-[#D97642] mb-2">{option.monthlyRate}<span className="text-lg text-gray-500">/mo</span></div>
                <div className="text-sm text-gray-600 mb-3">{option.coverage} coverage</div>
                <div className="text-sm text-gray-500 border-t border-gray-200 pt-3">
                  <strong>Best for:</strong> {option.bestFor}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Indianapolis Families Choose Term Life */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-4">
              Why Indianapolis Families Choose Term Life Insurance
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Term life insurance provides maximum protection at the lowest cost—perfect for Marion County families during their working years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-4xl text-[#D97642]`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A3A52] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#2A4A62] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Your Free Indianapolis Term Life Quote
            </h2>
            <p className="text-xl text-gray-200">
              See how affordable term life insurance can be for your Marion County family. No obligation, no pressure—just honest quotes.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-2xl">
            {iframeLoading && !iframeError && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97642]"></div>
              </div>
            )}

            {iframeError && (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-6">
                  Having trouble loading the form? No problem—contact us directly:
                </p>
                <div className="space-y-4">
                  <a
                    href="tel:+13176482983"
                    className="block bg-[#1A3A52] hover:bg-[#2A4A62] text-white px-6 py-3 rounded-lg font-bold transition-all"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call (317) 648-2983
                  </a>
                  <button
                    onClick={() => {
                      setIframeError(false);
                      setIframeLoading(true);
                    }}
                    className="block w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold transition-all"
                  >
                    Try Loading Form Again
                  </button>
                </div>
              </div>
            )}

            <iframe
              src="https://api.leadconnectorhq.com/widget/form/l6m1l2C1ZbhWcuOBNDGB"
              style={{
                width: '100%',
                height: '600px',
                border: 'none',
                display: iframeLoading || iframeError ? 'none' : 'block'
              }}
              onLoad={() => setIframeLoading(false)}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              title="Term Life Insurance Quote Form"
            />
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-200 mb-4">
              Prefer to speak with a local Indianapolis agent?
            </p>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center text-white hover:text-[#D97642] font-bold text-lg transition-colors"
            >
              <i className="ri-phone-line mr-2"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      {/* Indianapolis Neighborhoods Served */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-4">
              Serving Indianapolis Neighborhoods &amp; Suburbs
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We provide term life insurance to families throughout Marion County and surrounding communities.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {neighborhoods.map((neighborhood, index) => (
              <Link
                key={index}
                to={`/service-areas/${neighborhood.slug}`}
                className="bg-gray-50 hover:bg-[#D97642] hover:text-white border border-gray-200 rounded-lg p-4 text-center transition-all group"
              >
                <i className="ri-map-pin-line text-2xl text-[#D97642] group-hover:text-white mb-2"></i>
                <div className="font-medium text-gray-700 group-hover:text-white">
                  {neighborhood.name}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-4">
              Indianapolis Term Life Insurance FAQs
            </h2>
            <p className="text-lg text-gray-600">
              Common questions from Marion County families about term life insurance.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md overflow-hidden"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-bold text-[#1A3A52] pr-4">
                    {faq.question}
                  </h3>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-[#D97642] transition-transform flex-shrink-0 ${
                      openFAQ === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Cross-Links Section */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4">
              Explore More Life Insurance Options
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/term-life"
              className="bg-gray-50 hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-lg p-6 transition-all group"
            >
              <i className="ri-file-list-3-line text-3xl text-[#D97642] group-hover:text-white mb-3"></i>
              <h3 className="font-bold text-lg mb-2">Term Life Insurance</h3>
              <p className="text-sm text-gray-600 group-hover:text-gray-200">
                Learn about term life coverage options and benefits
              </p>
            </Link>

            <Link
              to="/life-insurance-indianapolis"
              className="bg-gray-50 hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-lg p-6 transition-all group"
            >
              <i className="ri-map-pin-line text-3xl text-[#D97642] group-hover:text-white mb-3"></i>
              <h3 className="font-bold text-lg mb-2">Life Insurance Indianapolis</h3>
              <p className="text-sm text-gray-600 group-hover:text-gray-200">
                All life insurance options for Indianapolis families
              </p>
            </Link>

            <Link
              to="/service-areas/indianapolis"
              className="bg-gray-50 hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-lg p-6 transition-all group"
            >
              <i className="ri-community-line text-3xl text-[#D97642] group-hover:text-white mb-3"></i>
              <h3 className="font-bold text-lg mb-2">Indianapolis Service Area</h3>
              <p className="text-sm text-gray-600 group-hover:text-gray-200">
                Coverage for all Marion County neighborhoods
              </p>
            </Link>

            <Link
              to="/wealth-strategy-call"
              className="bg-gray-50 hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-lg p-6 transition-all group"
            >
              <i className="ri-calendar-check-line text-3xl text-[#D97642] group-hover:text-white mb-3"></i>
              <h3 className="font-bold text-lg mb-2">Book Strategy Call</h3>
              <p className="text-sm text-gray-600 group-hover:text-gray-200">
                Free consultation with a local Indianapolis agent
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-[#1A3A52]">Schedule Your Free Consultation</h3>
              <button
                onClick={() => setShowBookingModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}